// src/components/layouts/root-layout.tsx
import { Outlet, useLocation } from "react-router-dom";
import { Navbar } from "@/components/navigation/navbar";
import { Footer } from "@/components/navigation/footer";
import { SampleDataBanner } from "@/components/sample-data-banner";
import ErrorBoundary from "@/components/error-boundary";
import { Toaster } from "@/components/ui/toaster";
import { DidYouKnowCard } from "@/components/did-you-know-card";
import { useDidYouKnow } from "@/hooks/use-did-you-know";
import { DevMemberBar } from "@/dev/DevMemberBar";

// Hook to safely use campaign data context
function useCampaignDataSafely() {
  try {
    // Dynamically import to avoid errors when context doesn't exist
    const { useCampaignData } = require("@/contexts/campaign-data");
    return useCampaignData();
  } catch {
    // Return safe defaults when CampaignDataProvider is not available
    return {
      useSampleData: false,
    };
  }
}

// Path configurations
const NO_BANNER_PATHS = [
  "/4cs",
  "/cpd",
  "/scary",
  "/test",
  "/prompt",
  "/brief",
  "/data",
  "/mastery",
  "/pmax",
  "/sales",
  "/slides",
];

// Paths where navbar should be hidden
const NO_NAVBAR_PATHS = ["/bss", "/sib"];

export default function RootLayout() {
  const location = useLocation();
  const campaignData = useCampaignDataSafely();
  const { useSampleData } = campaignData;
  const {
    currentTip,
    isVisible,
    dismissTip,
    showAnotherTip,
  } = useDidYouKnow();

  const isOnNoBannerPage = NO_BANNER_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );
  const shouldShowBanner = useSampleData && !isOnNoBannerPage;

  const isOnNoNavbarPage = NO_NAVBAR_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );

  const isDevMode = process.env.NODE_ENV === "development";

  return (
    <ErrorBoundary>
      <DevMemberBar />
      {/* Spacer for fixed DevMemberBar in dev mode */}
      {isDevMode && <div className="h-10" />}
      <div className="relative flex min-h-screen flex-col bg-background">
        {!isOnNoNavbarPage && <Navbar />}
        {shouldShowBanner && <SampleDataBanner />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!isOnNoNavbarPage && <Footer />}
      </div>
      <Toaster />
      {currentTip && (
        <DidYouKnowCard
          tip={currentTip}
          isVisible={isVisible}
          onDismiss={dismissTip}
          onShowAnother={showAnotherTip}
        />
      )}
    </ErrorBoundary>
  );
}
